import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { devWarn } from "@/lib/logger";

/**
 * ProfilePictureUploader — click the avatar to pick a new file, drag-and-drop supported.
 *
 * Backend contract:
 *   POST /api/uploads/profile-picture (multipart file field) → { ok, asset_id, picture, size }
 *   DELETE /api/uploads/profile-picture → { ok, removed }
 *   GET /api/uploads/{asset_id} → binary (public for profile_picture kind)
 *
 * `initialUrl` is what the parent renders from user.picture (may be legacy Google URL
 * or new /api/uploads/... path). `onChange(picture)` fires after upload/delete so the
 * parent can refresh the user card without a full remount.
 */
export const ProfilePictureUploader = ({ initialUrl = "", name = "", onChange }) => {
  const [url, setUrl] = useState(initialUrl);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Compute the fully-qualified URL for /api/uploads/... (relative paths need the backend base).
  const resolveUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
  };

  useEffect(() => { setUrl(initialUrl); }, [initialUrl]);

  const upload = async (file) => {
    if (!file) return;
    // Client-side check mirrors the backend whitelist so we give fast feedback.
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPG, PNG, WEBP or GIF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/uploads/profile-picture", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newPic = r?.data?.picture || "";
      setUrl(newPic);
      onChange?.(newPic);
      toast.success("Profile picture updated");
    } catch (err) {
      devWarn("[ProfilePicture] upload failed:", err?.response?.data || err?.message);
      toast.error(err?.response?.data?.detail || "Upload failed. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/uploads/profile-picture");
      setUrl("");
      onChange?.("");
      toast.success("Profile picture removed");
    } catch (err) {
      devWarn("[ProfilePicture] delete failed:", err?.message || err);
      toast.error("Remove failed. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const src = resolveUrl(url);

  return (
    <div className="flex items-center gap-4" data-testid="profile-picture-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) upload(f);
        }}
        onClick={() => inputRef.current?.click()}
        className={`relative h-20 w-20 shrink-0 border-2 flex items-center justify-center cursor-pointer overflow-hidden transition-colors ${
          dragOver ? "border-[#22c55e] bg-[#22c55e]/10" : "border-[#60a5fa] hover:border-[#93c5fd]"
        }`}
        data-testid="profile-picture-dropzone"
        title="Click or drag to upload"
      >
        {src ? (
          <img src={src} alt={name || "avatar"} className="h-full w-full object-cover" />
        ) : (
          <span className="text-[#60a5fa] font-heading font-black text-xl">{initials}</span>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <span className="text-[10px] tracking-[0.3em] uppercase text-white font-mono">…</span>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono">// avatar</div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#60a5fa] hover:bg-[#93c5fd] disabled:bg-zinc-600 px-3 py-1.5 font-black transition-colors"
            data-testid="profile-picture-upload-btn"
          >
            {url ? "Change" : "Upload"}
          </button>
          {url && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 border border-zinc-700 hover:border-red-500 hover:text-red-400 disabled:opacity-50 px-3 py-1.5 font-black transition-colors"
              data-testid="profile-picture-remove-btn"
            >
              Remove
            </button>
          )}
        </div>
        <div className="text-[10px] text-zinc-500 font-mono">JPG, PNG, WEBP or GIF · max 5 MB</div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        data-testid="profile-picture-file-input"
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </div>
  );
};

export default ProfilePictureUploader;
